/*
 *   File : edit-risk-score.js
 *   Author : https://evoqins.com
 *   Description : edit risk score component
 *   Integrations : risk/list-score, risk/edit-score
 *   Version : 1.2.0
 */
import React, { useEffect, useState } from "react";
import { Input } from "../../Components/FormElements";
import { PrimaryButton } from "../../Components/Buttons";
import { useDispatch } from "react-redux";
import {
  setNavLink,
  setPageTitle,
  setSearchEnabled,
} from "../../Redux/Ui/ui.slice";
import { useNavigate } from "react-router-dom";
import { APIService } from "../../Service";
import { toast } from "react-toastify";
import { Loader } from "../../Components/Other";

export default function EditRiskScore({ tabChange }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  /* ---------- PAGE SETTINGS ---------- */
  useEffect(() => {
    dispatch(setPageTitle(["Manage Risk Score", "Edit"]));
    dispatch(setSearchEnabled(false));
    dispatch(setNavLink(["/manage-risk-score/risk"]));
  }, []);

  /* ---------- LOCAL STATE ---------- */
  const [riskData, setRiskData] = useState([]);
  const [loading, setLoading] = useState(false);

  /* ---------- FETCH RISK SCORES ---------- */
  useEffect(() => {
    _fetchRiskScore();
  }, []);

  const _fetchRiskScore = async () => {
    setLoading(true);
    try {
      const res = await APIService(false, "risk/list-score");

      if (res.status_code === 200 && Array.isArray(res.data)) {
        const formatted = res.data.map((item) => ({
          id: item.id,
          riskType: item.name || "",
          from: item.score_from ?? "",
          to: item.score_to ?? "",
          errors: {
            from: "",
            to: "",
          },
        }));

        setRiskData(formatted);
      }
    } catch (error) {
      console.error("risk/list-score error:", error);
    } finally {
      setLoading(false);
    }
  };

  /* ---------- INPUT HANDLER ---------- */
  const handleScoreChange = (index, field, value) => {
    setRiskData((prev) => {
      const updated = [...prev];

      updated[index] = {
        ...updated[index],
        [field]: value === "" ? "" : Number(value),
        errors: {
          ...updated[index].errors,
          [field]: value === "" ? "" : "",
        },
      };

      return updated;
    });
  };

  /* ---------- SAVE HANDLER ---------- */
  const handleSave = async () => {
    let hasError = false;

    const validatedData = riskData.map((item) => {
      const errors = {
        from: item.from === "" ? "Score From is required" : "",
        to: item.to === "" ? "Score To is required" : "",
      };

      if (errors.from || errors.to) {
        hasError = true;
      }

      return {
        ...item,
        errors,
      };
    });

    if (hasError) {
      setRiskData(validatedData);
      toast.error("Please fill all required fields");
      return;
    }

    const payload = {
      styles: validatedData.map((item) => ({
        id: item.id,
        name: item.riskType,
        score_from: Number(item.from),
        score_to: Number(item.to),
      })),
    };

    try {
      setLoading(true);
      const res = await APIService(true, "risk/edit-score", payload);

      if (res.status_code === 200) {
        toast.success(res.message);
        navigate("/manage-risk-score");
        tabChange();
      }
    } catch (error) {
      console.error("risk/edit-score error:", error);
    } finally {
      setLoading(false);
    }
  };

  /* ---------- RENDER ---------- */
  if (loading) return <Loader />;
  return (
    <>
      <div className="e-glass-card rounded-16 p-3 d-flex flex-column">
        <h5 className="fs-16 fw-500 text-eerie-black mb-0">
          Edit Risk Profile Score
        </h5>

        <div className="d-flex pt-20">
          <div className="row w-100">
            {riskData.map((item, index) => (
              <div className="col-12 mb-3" key={item.id}>
                <div className="row">
                  {/* RISK PROFILE (READ ONLY) */}
                  <div className="col-6">
                    <Input
                      id={`risk-${item.id}`}
                      type="text"
                      label="Risk Profile"
                      require={true}
                      value={item.riskType}
                      className="w-100 px-3 py-10"
                      disabled={true}
                    />
                  </div>

                  {/* SCORE FROM */}
                  <div className="col-3">
                    <Input
                      id={`score-from-${item.id}`}
                      type="number"
                      label="Score From"
                      require={true}
                      value={item.from}
                      className="w-100 px-3 py-10"
                      error={item.errors?.from}
                      handleChange={(val) =>
                        handleScoreChange(index, "from", val)
                      }
                    />
                  </div>

                  {/* SCORE TO */}
                  <div className="col-3">
                    <Input
                      id={`score-to-${item.id}`}
                      type="number"
                      label="Score To"
                      require={true}
                      value={item.to}
                      className="w-100 px-3 py-10"
                      error={item.errors?.to}
                      handleChange={(val) =>
                        handleScoreChange(index, "to", val)
                      }
                    />
                  </div>
                </div>
              </div>
            ))}

            {!loading && riskData.length === 0 && (
              <p className="fs-14 text-center w-100">No risk scores found</p>
            )}
          </div>
        </div>
      </div>

      {/* BUTTONS */}
      <div className="row">
        <div className="offset-lg-2 col-lg-10 col-12">
          <div className="row row-cols-2 pt-24">
            <div className="col">
              <PrimaryButton
                name="Cancel"
                ClassName="rounded-pill bg-transparent border-oxford-blue lh-24 py-3 text-oxford-blue w-100 fs-16 fw-500"
                onPress={() => {
                  navigate("/manage-risk-score/risk");
                  tabChange();
                }}
              />
            </div>

            <div className="col">
              <PrimaryButton
                name="Save changes"
                ClassName="rounded-pill bg-oxford-blue lh-24 py-3 text-white w-100 fs-16 fw-500"
                onPress={handleSave}
                disabled={loading}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
